import { OVERLAY_LINE_WIDTH } from '@/trendData/trendData.constants';

export const MIN_OVERLAY_LINE_HEIGHT = 1;

export type OverlayLayoutForLevel = {
  capsuleHeight: number;
  capsuleTopInset: number;
  overlayLineWidth: number;
};

/**
 * Wedding-cake overlay layout within a condition line:
 * - Back overlay (lowest index): full line height, flush to top and bottom
 * - Each front overlay: shorter height with equal inset on top and bottom vs the layer behind
 *
 * Scales inset steps down when the line is shorter than the default overlay width allows.
 */
export function computeOverlayLayoutForLevel({
  heightPerLevel,
  overlay,
  maxOverlayInRow,
  conditionsWithoutCapsulesCount,
}: {
  heightPerLevel: number;
  overlay: number;
  maxOverlayInRow: number;
  conditionsWithoutCapsulesCount: number;
}): OverlayLayoutForLevel {
  const overlayIndex = Math.max(0, overlay - conditionsWithoutCapsulesCount - 1);
  const maxOverlayIndex = Math.max(0, maxOverlayInRow - conditionsWithoutCapsulesCount - 1);

  if (maxOverlayIndex <= 0) {
    return {
      capsuleHeight: heightPerLevel,
      capsuleTopInset: 0,
      overlayLineWidth: OVERLAY_LINE_WIDTH,
    };
  }

  const scaledWidth = Math.max(0, (heightPerLevel - MIN_OVERLAY_LINE_HEIGHT) / maxOverlayIndex);
  const overlayLineWidth = Math.min(OVERLAY_LINE_WIDTH, scaledWidth);
  const overlayInset = overlayIndex * overlayLineWidth;

  return {
    capsuleHeight: Math.max(MIN_OVERLAY_LINE_HEIGHT, heightPerLevel - overlayInset),
    capsuleTopInset: overlayInset / 2,
    overlayLineWidth,
  };
}

export function getOverlayOffset(
  overlay: number,
  conditionsWithoutCapsulesCount: number,
  overlayLineWidth = OVERLAY_LINE_WIDTH,
): number {
  const overlayIndex = overlay - conditionsWithoutCapsulesCount - 1;
  if (overlayIndex <= 0) {
    return 0;
  }

  return (overlayIndex * overlayLineWidth) / 2;
}
